import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import { FiCopy, FiLogOut } from "react-icons/fi";

// Updated contract addresses
const FAUCET_CONTRACT_ADDRESS = "0xCa602D9E45E1Ed25105Ee43643ea936B8e2Fd6B7";
const PRIOR_TOKEN_ADDRESS = "0xc19Ec2EEBB009b2422514C51F9118026f1cD89ba";

// Updated Faucet ABI (simplified from your provided ABI)
const FAUCET_ABI = [
  {
    "inputs": [],
    "name": "claimTokens",
    "outputs": [],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "",
        "type": "address"
      }
    ],
    "name": "lastClaimTime",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "",
        "type": "uint256"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "claimCooldown",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "",
        "type": "uint256"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "claimAmount",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "",
        "type": "uint256"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  }
];

// Updated PRIOR Token ABI (simplified)
const PRIOR_TOKEN_ABI = [
  {
    "constant": true,
    "inputs": [{"name": "_owner", "type": "address"}],
    "name": "balanceOf",
    "outputs": [{"name": "balance", "type": "uint256"}],
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "decimals",
    "outputs": [{"name": "", "type": "uint8"}],
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "symbol",
    "outputs": [{"name": "", "type": "string"}],
    "type": "function"
  }
];

const Faucet = () => {
  const [account, setAccount] = useState(null);
  const [ethBalance, setEthBalance] = useState("0.0");
  const [priorBalance, setPriorBalance] = useState("0.0");
  const [lastClaimTime, setLastClaimTime] = useState(null);
  const [cooldownTime, setCooldownTime] = useState(24 * 60 * 60);
  const [timeLeft, setTimeLeft] = useState(0);
  const [notification, setNotification] = useState(null);
  const [isCopied, setIsCopied] = useState(false);
  const [tokenData, setTokenData] = useState({ decimals: 18, symbol: "PRIOR" });
  const [claimableAmount, setClaimableAmount] = useState("0");

  // Show notification
  const showNotification = (message, isSuccess = true) => {
    setNotification({ message, isSuccess });
    setTimeout(() => setNotification(null), 5000);
  };

  // Copy address to clipboard
  const copyAddress = () => {
    navigator.clipboard.writeText(account);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  // Connect Wallet
  const connectWallet = async () => {
    if (window.ethereum) {
      try {
        const provider = new ethers.BrowserProvider(window.ethereum);
        const accounts = await provider.send("eth_requestAccounts", []);
        setAccount(accounts[0]);
        fetchTokenData(provider);
        fetchBalances(accounts[0]);
        checkCooldown(accounts[0]);
        fetchClaimableAmount(provider);
      } catch (error) {
        console.error("Error connecting wallet:", error);
        showNotification("Error connecting wallet", false);
      }
    } else {
      showNotification("Please install MetaMask!", false);
    }
  };

  // Fetch token metadata
  const fetchTokenData = async (provider) => {
    try {
      const tokenContract = new ethers.Contract(PRIOR_TOKEN_ADDRESS, PRIOR_TOKEN_ABI, provider);
      const [decimals, symbol] = await Promise.all([
        tokenContract.decimals(),
        tokenContract.symbol()
      ]);
      setTokenData({ decimals: Number(decimals), symbol });
    } catch (error) {
      console.error("Error fetching token data:", error);
    }
  };

  // Fetch claimable amount
  const fetchClaimableAmount = async (provider) => {
    try {
      const faucetContract = new ethers.Contract(FAUCET_CONTRACT_ADDRESS, FAUCET_ABI, provider);
      const amount = await faucetContract.claimAmount();
      setClaimableAmount(ethers.formatUnits(amount, tokenData.decimals));
    } catch (error) {
      console.error("Error fetching claimable amount:", error);
    }
  };

  // Disconnect Wallet
  const disconnectWallet = () => {
    setAccount(null);
    setEthBalance("0.0");
    setPriorBalance("0.0");
    setLastClaimTime(null);
    setTimeLeft(0);
  };

  // Fetch both ETH and PRIOR balances
  const fetchBalances = async (walletAddress) => {
    if (!walletAddress) return;
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      
      // Fetch ETH balance
      const ethBal = await provider.getBalance(walletAddress);
      setEthBalance(ethers.formatEther(ethBal));
      
      // Fetch PRIOR token balance
      const tokenContract = new ethers.Contract(PRIOR_TOKEN_ADDRESS, PRIOR_TOKEN_ABI, provider);
      const priorBal = await tokenContract.balanceOf(walletAddress);
      setPriorBalance(ethers.formatUnits(priorBal, tokenData.decimals));
    } catch (error) {
      console.error("Error fetching balances:", error);
    }
  };

  // Check last claim time and cooldown
  const checkCooldown = async (walletAddress) => {
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const contract = new ethers.Contract(FAUCET_CONTRACT_ADDRESS, FAUCET_ABI, provider);
      
      const lastClaimed = await contract.lastClaimTime(walletAddress);
      setLastClaimTime(Number(lastClaimed));

      const cooldown = await contract.claimCooldown();
      setCooldownTime(Number(cooldown));

      const currentTime = Math.floor(Date.now() / 1000);
      const remainingTime = Math.max(0, Number(lastClaimed) + Number(cooldown) - currentTime);
      setTimeLeft(remainingTime);
    } catch (error) {
      console.error("Error fetching cooldown:", error);
    }
  };

  // Claim Faucet Tokens
  const claimTokens = async () => {
    if (!account) return;

    const currentTime = Math.floor(Date.now() / 1000);
    if (timeLeft > 0) {
      showNotification(`Already claimed! Come back in ${formatTime(timeLeft)}.`, false);
      return;
    }

    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const faucetContract = new ethers.Contract(FAUCET_CONTRACT_ADDRESS, FAUCET_ABI, signer);
      
      const tx = await faucetContract.claimTokens();
      await tx.wait();
      
      showNotification(`Successfully claimed ${claimableAmount} ${tokenData.symbol} tokens!`, true);

      setLastClaimTime(currentTime);
      setTimeLeft(cooldownTime);
      fetchBalances(account); // Refresh balances after claim
    } catch (error) {
      console.error("Error claiming tokens:", error);
      showNotification("Claim failed. Please try again later.", false);
    }
  };

  // Update countdown timer
  useEffect(() => {
    if (lastClaimTime) {
      const interval = setInterval(() => {
        const currentTime = Math.floor(Date.now() / 1000);
        const remaining = Math.max(0, cooldownTime - (currentTime - lastClaimTime));
        setTimeLeft(remaining);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [lastClaimTime, cooldownTime]);

  // Format time in HH:MM:SS
  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}h ${m}m ${s}s`;
  };

  return (
    <div className="min-h-screen bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      {notification && (
        <div className={`fixed top-5 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded-md shadow-lg ${notification.isSuccess ? "bg-green-600" : "bg-red-600"} text-white text-center max-w-md w-full`}>
          {notification.message}
        </div>
      )}

      <div className="max-w-md mx-auto">
        {/* Header Card */}
        <div className="bg-gray-800 rounded-xl shadow-lg overflow-hidden mb-8 border border-gray-700">
          <div className="p-6 sm:p-8">
            <h1 className="text-3xl font-bold text-center text-[#00df9a] mb-2">PRIOR FAUCET</h1>
            <p className="text-gray-300 text-center mb-6">Get ${tokenData.symbol} test tokens for Base Sepolia</p>
            
            {!account ? (
              <button 
                onClick={connectWallet} 
                className="w-full bg-[#00df9a] hover:bg-[#00c78a] text-black font-bold py-3 px-4 rounded-lg transition duration-200"
              >
                Connect Wallet
              </button>
            ) : (
              <div className="flex justify-center">
                <button 
                  onClick={disconnectWallet}
                  className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-lg transition duration-200"
                >
                  <FiLogOut /> Disconnect Wallet
                </button>
              </div>
            )}
          </div>
        </div>

        {account && (
          <div className="space-y-6">
            {/* Wallet Info Card */}
            <div className="bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-700">
              <div className="p-6 sm:p-8">
                <h2 className="text-xl font-semibold text-[#00df9a] mb-4">Wallet Information</h2>
                <div className="space-y-4">
                  <div>
                    <p className="text-gray-400 mb-1">Wallet Address</p>
                    <div className="flex items-center justify-between bg-gray-700 p-3 rounded-lg">
                      <span className="text-white truncate">{account}</span>
                      <button 
                        onClick={copyAddress}
                        className="text-gray-400 hover:text-[#00df9a] transition duration-200 ml-2"
                        title="Copy to clipboard"
                      >
                        <FiCopy />
                      </button>
                    </div>
                    {isCopied && <p className="text-green-400 text-sm mt-1">Address copied!</p>}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-700 p-4 rounded-lg">
                      <p className="text-gray-400 text-sm">ETH Balance</p>
                      <p className="text-white font-medium">{parseFloat(ethBalance).toFixed(4)} ETH</p>
                    </div>
                    <div className="bg-gray-700 p-4 rounded-lg">
                      <p className="text-gray-400 text-sm">{tokenData.symbol} Balance</p>
                      <p className="text-white font-medium">{parseFloat(priorBalance).toFixed(2)} {tokenData.symbol}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Faucet Card */}
            <div className="bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-700">
              <div className="p-6 sm:p-8">
                <h2 className="text-xl font-semibold text-[#00df9a] mb-4">Claim Tokens</h2>
                <div className="space-y-6">
                  {timeLeft > 0 ? (
                    <div className="text-center">
                      <p className="text-red-400 mb-4">Next claim available in:</p>
                      <div className="text-2xl font-bold text-white bg-gray-700 py-3 px-6 rounded-lg">
                        {formatTime(timeLeft)}
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-gray-300 text-center">You can claim {claimableAmount} {tokenData.symbol} tokens now</p>
                      <button 
                        onClick={claimTokens}
                        className="w-full bg-[#00df9a] hover:bg-[#00c78a] text-black font-bold py-3 px-4 rounded-lg transition duration-200"
                      >
                        Claim {claimableAmount} {tokenData.symbol} Tokens
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Network Info Card */}
            <div className="bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-700">
              <div className="p-6 sm:p-8">
                <h2 className="text-xl font-semibold text-[#00df9a] mb-4">Network Information</h2>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Network:</span>
                    <span className="text-white">Base Sepolia</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Cooldown:</span>
                    <span className="text-white">{formatTime(cooldownTime)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Token:</span>
                    <span className="text-white">{tokenData.symbol}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Faucet;